/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import NGOInvite from './NGOInvite';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

interface Project {
  id: string;
  name: string;
  summary: string;
  status: string;
  implementation_type: 'ngo_led' | 'direct';
  thematic_area?: string | null;
  created_at: string;
  assigned_ngos?: Array<{
    ngo_id: string;
    status: string;
    ngos: {
      legal_name: string;
    };
  }>;
}

const CompanyProjects: React.FC = () => {
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [error, setError] = useState('');
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [projectToRequestChanges, setProjectToRequestChanges] = useState<Project | null>(null);
  const [requestChangesComment, setRequestChangesComment] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRequestingChanges, setIsRequestingChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<'projects' | 'ngos'>('ngos');

  useEffect(() => {
    if (companyId) {
      loadProjects();
    }
  }, [companyId]);

  const loadProjects = async () => {
    if (!companyId) return;

    setLoading(true);
    setError('');

    try {
      // Fetch all projects for this company
      const { data, error: projectsError } = await supabase
        .from('projects')
        .select(`
          id,
          name,
          summary,
          status,
          implementation_type,
          thematic_area,
          created_at,
          project_ngos (
            ngo_id,
            status,
            ngos (
              legal_name
            )
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (projectsError) throw projectsError;

      // Transform the data to match our interface
      const transformedProjects: Project[] = (data || []).map((project: any) => ({
        id: project.id,
        name: project.name,
        summary: project.summary,
        status: project.status,
        implementation_type: project.implementation_type || 'ngo_led',
        thematic_area: project.thematic_area,
        created_at: project.created_at,
        assigned_ngos: project.project_ngos || []
      }));

      setProjects(transformedProjects);
    } catch (err: any) {
      setError(err.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;

    setIsDeleting(true);
    setError('');

    try {
      const { error: deleteError } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectToDelete.id);

      if (deleteError) throw deleteError;

      // Update the local state to remove the deleted project
      setProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
      setProjectToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete project');
      setProjectToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!projectToRequestChanges || !requestChangesComment.trim()) return;

    setIsRequestingChanges(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update project status to 'draft' and store change request
      const { error: updateError } = await supabase
        .from('projects')
        .update({
          status: 'draft',
          changes_requested: {
            requested_by: user.id,
            comment: requestChangesComment,
            requested_at: new Date().toISOString()
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', projectToRequestChanges.id);

      if (updateError) throw updateError;

      // Update all assigned NGOs to 'changes_requested' status
      const { error: ngoUpdateError } = await supabase
        .from('project_ngos')
        .update({
          status: 'changes_requested',
          requested_changes: requestChangesComment
        })
        .eq('project_id', projectToRequestChanges.id);

      if (ngoUpdateError) throw ngoUpdateError;

      // Update local state
      setProjects(prev => prev.map(p => 
        p.id === projectToRequestChanges.id 
          ? { ...p, status: 'draft' } 
          : p
      ));
      
      setProjectToRequestChanges(null);
      setRequestChangesComment('');
      alert('Changes requested successfully. The project has been reverted to draft status.');
    } catch (err: any) {
      setError(err.message || 'Failed to request changes');
    } finally {
      setIsRequestingChanges(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: '#6b7280',
      assigned: '#3b82f6',
      accepted: '#10b981',
      pre_start: '#f59e0b',
      active: '#10b981',
      closed: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: 'Draft',
      assigned: 'Assigned to NGO',
      accepted: 'Accepted by Company',
      pre_start: 'Pre-Start',
      active: 'Active',
      closed: 'Closed'
    };
    return labels[status] || status;
  };

  const renderProjectActions = (project: Project) => (
    <div className="project-card-actions">
      {project.implementation_type === 'direct' ? (
        <>
          <Link
            to={`/client/company/projects/${project.id}/review?edit=true`}
            className="btn btn-sm btn-primary"
          >
            Edit
          </Link>
          <Link
            to={`/client/company/projects/${project.id}/review`}
            className="btn btn-sm btn-outline"
          >
            View
          </Link>
        </>
      ) : project.status === 'draft' ? (
        <Link
          to={`/client/company/projects/${project.id}/review`}
          className="btn btn-sm btn-primary"
        >
          Review
        </Link>
      ) : (
        <>
          <Link
            to={`/client/company/projects/${project.id}/review?edit=true`}
            className="btn btn-sm btn-primary"
          >
            Edit
          </Link>

          {project.status === 'pre_start' && (
            <Link
              to={`/client/company/projects/${project.id}/checklist`}
              className="btn btn-sm btn-success"
              style={{
                background: '#10b981',
                color: 'white',
                cursor: 'pointer',
                textDecoration: 'none'
              }}
            >
              Pre-Start Checklist
            </Link>
          )}
          <Link
            to={`/client/company/projects/${project.id}/review`}
            className="btn btn-sm btn-outline"
          >
            View
          </Link>
        </>
      )}

      <button
        onClick={() => setProjectToDelete(project)}
        className="btn btn-sm btn-outline"
        style={{ borderColor: '#ef4444', color: '#ef4444' }}
        title="Delete Project"
      >
        Delete
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="projects-page-container">
      <div className="projects-page-header">
        <h1>NGO Partners & Projects</h1>
        {activeTab === 'projects' && projects.length > 0 && (
          <Link to={`/client/company/projects/create`} className="btn btn-primary">
            + Create New Project
          </Link>
        )}
      </div>

      <div style={{
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        color: '#1e3a8a',
        padding: '1rem 1.5rem',
        borderRadius: '8px',
        fontSize: '0.9rem',
        marginBottom: '2rem',
        lineHeight: '1.6'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>💡</span>
          <strong style={{ fontSize: '1rem' }}>Programs Workflow Guide</strong>
        </div>
        <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
          <li><strong>NGO Partners:</strong> Invite NGOs, review their profiles, and verify them for collaboration.</li>
          <li><strong>Projects:</strong> Create, view, and manage all your CSR projects.</li>
        </ul>
      </div>

      {error && (
        <div className="toast toast-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '2px solid var(--border)' }}>
        {(['ngos', 'projects'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === tab ? '2px solid var(--primary)' : '2px solid transparent',
              color: activeTab === tab ? 'var(--primary)' : 'var(--text-secondary)',
              fontWeight: activeTab === tab ? '600' : '500',
              fontSize: '1.1rem',
              cursor: 'pointer',
              marginBottom: '-2px'
            }}
          >
            {tab === 'ngos' ? 'NGO Partners' : 'Projects'}
          </button>
        ))}
      </div>

      {activeTab === 'ngos' && (
        <NGOInvite />
      )}

      {activeTab === 'projects' && (
        projects.length === 0 ? (
          <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
            <h2 style={{ marginBottom: '1rem' }}>No Projects Yet</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
              Create your first project and assign it to NGOs to get started.
            </p>
            <Link to={`/client/company/projects/create`} className="btn btn-primary">
              Create New Project
            </Link>
          </div>
        ) : (
        <>
          {/* Mobile Card List View (< 768px) */}
          <div className="projects-mobile-cards">
            {projects.map((project) => (
              <div key={project.id} className="project-card-item">
                <div className="project-card-header">
                  <h3 className="project-card-title">{project.name}</h3>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.65rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      backgroundColor: `${getStatusColor(project.status)}15`,
                      color: getStatusColor(project.status),
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                  >
                    {getStatusLabel(project.status)}
                  </span>
                </div>

                {project.summary && (
                  <p className="project-card-summary">{project.summary}</p>
                )}

                <div className="project-card-details">
                  <div className="project-card-detail-row">
                    <span className="project-card-detail-label">Implementation:</span>
                    <span className="project-card-detail-value">
                      {project.implementation_type === 'direct' ? (
                        <span style={{
                          color: 'var(--primary)',
                          fontWeight: '500',
                          background: 'var(--primary-light)',
                          padding: '0.15rem 0.5rem',
                          borderRadius: '4px',
                          display: 'inline-block'
                        }}>
                          Direct Implementation
                        </span>
                      ) : (
                        'NGO-Led'
                      )}
                    </span>
                  </div>

                  <div className="project-card-detail-row">
                    <span className="project-card-detail-label">Assigned NGOs:</span>
                    <span className="project-card-detail-value">
                      {project.implementation_type === 'direct' ? (
                        <span style={{ color: 'var(--muted)' }}>N/A (Direct)</span>
                      ) : project.assigned_ngos && project.assigned_ngos.length > 0 ? (
                        project.assigned_ngos.map(pn => pn.ngos?.legal_name || 'NGO').join(', ')
                      ) : (
                        <span style={{ color: 'var(--muted)' }}>Not assigned</span>
                      )}
                    </span>
                  </div>

                  <div className="project-card-detail-row">
                    <span className="project-card-detail-label">Created:</span>
                    <span className="project-card-detail-value">
                      {new Date(project.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {renderProjectActions(project)}
              </div>
            ))}
          </div>

          {/* Desktop Table View (>= 768px) */}
          <div className="table-container projects-desktop-table">
            <table className="table" style={{ minWidth: '950px' }}>
              <thead>
                <tr>
                  <th style={{ width: '22%', minWidth: '180px' }}>Project Name</th>
                  <th style={{ width: '25%', minWidth: '220px' }}>Summary</th>
                  <th style={{ width: '12%', minWidth: '110px' }}>Status</th>
                  <th style={{ width: '18%', minWidth: '150px' }}>Assigned NGOs</th>
                  <th style={{ width: '10%', minWidth: '100px' }}>Created</th>
                  <th style={{ width: '13%', minWidth: '160px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr key={project.id}>
                    <td>
                      <strong>{project.name}</strong>
                    </td>
                    <td>
                      <div style={{ whiteSpace: 'normal', fontSize: '0.8125rem', lineHeight: '1.4', wordBreak: 'break-word' }}>
                        {project.summary || 'No summary'}
                      </div>
                    </td>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          backgroundColor: `${getStatusColor(project.status)}15`,
                          color: getStatusColor(project.status)
                        }}
                      >
                        {getStatusLabel(project.status)}
                      </span>
                    </td>
                    <td>
                      {project.implementation_type === 'direct' ? (
                        <span style={{
                          color: 'var(--primary)',
                          fontSize: '0.8125rem',
                          fontWeight: '500',
                          background: 'var(--primary-light)',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          display: 'inline-block',
                          maxWidth: '160px',
                          lineHeight: '1.3',
                          whiteSpace: 'normal'
                        }}>
                          Direct Implementation
                        </span>
                      ) : project.assigned_ngos && project.assigned_ngos.length > 0 ? (
                        <div>
                          {project.assigned_ngos.map((pn: any, idx: number) => (
                            <div key={idx} style={{ marginBottom: '0.25rem' }}>
                              <span style={{ fontSize: '0.8125rem', display: 'inline-block', maxWidth: '160px', lineHeight: '1.3', whiteSpace: 'normal' }}>
                                {pn.ngos?.legal_name || 'NGO'}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Not assigned</span>
                      )}
                    </td>
                    <td>
                      {new Date(project.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      {renderProjectActions(project)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
        )
      )}


      {/* Delete Confirmation Modal */}
      {projectToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--card-bg, #ffffff)',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h3 style={{ marginTop: 0, color: '#ef4444', fontSize: '1.25rem', fontWeight: 600 }}>Delete Project?</h3>
            <p style={{ marginTop: '1rem', color: 'var(--text)' }}>
              Are you sure you want to delete the project <strong>{projectToDelete.name}</strong>?
            </p>
            <div style={{
              marginTop: '1rem',
              marginBottom: '2rem',
              padding: '1rem',
              backgroundColor: '#fee2e2',
              borderRadius: '8px',
              border: '1px solid #fecaca'
            }}>
              <p style={{ color: '#b91c1c', fontSize: '0.875rem', margin: 0, fontWeight: 500 }}>
                ⚠️ Warning: This action cannot be undone.
              </p>
              <p style={{ color: '#991b1b', fontSize: '0.875rem', marginTop: '0.5rem', marginBottom: 0 }}>
                Deleting this project will also permanently delete all related items, including:
              </p>
              <ul style={{ color: '#991b1b', fontSize: '0.875rem', marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                <li>Assigned NGOs and their association status</li>
                <li>Project metrics progress and goals</li>
                <li>Fund requests and disbursement records</li>
                <li>Project-specific compliance documents</li>
                <li>KPI reports and utilization certificates</li>
                <li>Project milestones and calendar events</li>
              </ul>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button
                onClick={() => setProjectToDelete(null)}
                className="btn btn-outline"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProject}
                className="btn btn-primary"
                style={{ background: '#ef4444', borderColor: '#ef4444', color: 'white' }}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Changes Modal */}
      {projectToRequestChanges && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--card-bg, #ffffff)',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h3 style={{ marginTop: 0, color: 'var(--primary)', fontSize: '1.25rem', fontWeight: 600 }}>Request Changes from NGO</h3>
            <p style={{ marginTop: '1rem', color: 'var(--text)' }}>
              Explain what details need to be updated in <strong>{projectToRequestChanges.name}</strong>. The project will be reverted to draft status.
            </p>
            
            <div className="form-group" style={{ marginTop: '1.5rem' }}>
              <label>Comments / Requirements *</label>
              <textarea
                value={requestChangesComment}
                onChange={(e) => setRequestChangesComment(e.target.value)}
                placeholder="e.g. Please update the budget breakdown or refine the target beneficiaries..."
                rows={5}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border)' }}
                required
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
              <button
                onClick={() => {
                  setProjectToRequestChanges(null);
                  setRequestChangesComment('');
                }}
                className="btn btn-outline"
                disabled={isRequestingChanges}
              >
                Cancel
              </button>
              <button
                onClick={handleRequestChanges}
                className="btn btn-primary"
                disabled={isRequestingChanges || !requestChangesComment.trim()}
              >
                {isRequestingChanges ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyProjects;
